import React, {
  MouseEvent, ChangeEvent,
} from "react"
import {
  Table as ChakraTable, Tbody, Tr,
  Box,
  Spinner,
  Td,
} from "@chakra-ui/react"
import { GuestSummary } from "sharedTypes"
import {
  CELL_PADDING_X,
  TABLE_HEADER_HEIGHT_PX,
} from "./helpers"
import { useDataTable } from "./useDataTable"
import DataTableHead from "./DataTableHead"
import DataTableRow from "./DataTableRow"

type Props = {
  guests: GuestSummary[];
  order: string;
  allSelected: boolean;
  multipleSelect?: boolean;
  disabledGuestIds: string[];
  disableMainGuests: boolean;
  filtered: boolean;
  timezone: string;
  onScrollEnd?: () => void;
  toggleSelectAll: () => void;
  onSelectionChange: (
    event: MouseEvent<HTMLElement> | ChangeEvent<HTMLInputElement>,
    guest: GuestSummary,
    checked?: boolean,
  ) => void;
  onClick: (event: MouseEvent<HTMLElement>, guest: GuestSummary) => void;
  onSetOrder: (order: string) => void;
  loading: boolean;
  loadingMore: boolean;
  activeGuestId?: string;
}

const DataTable = ({
  guests,
  order,
  allSelected,
  multipleSelect = false,
  disabledGuestIds = [],
  disableMainGuests,
  filtered,
  timezone,
  onScrollEnd,
  toggleSelectAll,
  onSelectionChange,
  onClick,
  onSetOrder,
  loading = false,
  loadingMore = false,
  activeGuestId,
}: Props) => {
  const {
    tableContainerRef,
    columnData,
    getColumnWidth,
    table,
    rowVirtualizer,
  } = useDataTable({
    guests,
    order,
    allSelected,
    multipleSelect,
    disabledGuestIds,
    disableMainGuests,
    filtered,
    timezone,
    onScrollEnd,
    toggleSelectAll,
    onSelectionChange,
    onSetOrder,
  })

  const meta = table.options.meta!
  const { rows } = table.getRowModel()

  return (
    <Box position="relative" width="100%" height="100%">
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="whiteAlpha.800"
          zIndex={4}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="lg" color="orange.500" />
        </Box>
      )}

      <Box
        ref={tableContainerRef}
        width="100%"
        height="100%"
        overflow="overlay"
        overscrollBehavior="none"
        sx={{
          // Mirrors SimpleBar's visible-state thumb so this native-scroll
          // container matches the SimpleBar containers (Combobox / Menu).
          scrollbarWidth: "thin",
          scrollbarColor: "rgb(127,127,127) transparent",
          "@supports not (overflow: overlay)": {
            overflow: "auto",
          },
          "&::-webkit-scrollbar": {
            width: "11px",
            height: "11px",
            background: "transparent",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgb(127,127,127)",
            backgroundClip: "padding-box",
            border: "2px solid transparent",
            borderRadius: "7px",
          },
          "&::-webkit-scrollbar-corner": {
            background: "transparent",
          },
        }}
      >
        <ChakraTable
          variant="unstyled"
          layout="fixed"
          width={columnData.shouldScroll ? `${columnData.totalWidth}px` : "100%"}
          minWidth={columnData.shouldScroll ? `${columnData.totalWidth}px` : "100%"}
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          overflow="clip"
          userSelect="none"
          role="table"
          aria-rowcount={rows.length}
        >
          <DataTableHead
            table={table}
            getColumnWidth={getColumnWidth}
            cellPaddingX={CELL_PADDING_X}
            headerHeightPx={TABLE_HEADER_HEIGHT_PX}
          />

          <Tbody
            style={{
              display: "block",
              position: "relative",
              minHeight: "1px",
            }}
            role="rowgroup"
          >
            {/* Spacer row to establish total height for virtualization */}
            <Tr
              aria-hidden="true"
              role="presentation"
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                display: "block",
                position: "relative",
                pointerEvents: "none",
                visibility: "hidden",
              }}
            >
              <Td />
            </Tr>

            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const row = rows[virtualItem.index]
              if (!row || !row.original) return null

              const isSelected = meta.selectedGuestIdsSet.has(row.original.id)
                || meta.allSelected
              const isActive = row.original.id === activeGuestId

              return (
                <DataTableRow
                  key={virtualItem.key}
                  virtualItem={virtualItem}
                  row={row}
                  isSelected={isSelected}
                  isActive={isActive}
                  getColumnWidth={getColumnWidth}
                  onClick={onClick}
                  cellPaddingX={CELL_PADDING_X}
                />
              )
            })}

          </Tbody>
        </ChakraTable>

        {loadingMore && (
          <Box
            width="100%"
            height="40px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Spinner size="md" color="gray.500" />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default DataTable
